import React, { useState, Fragment } from "react";

const PrivacyPolicy = () => {
  const [openSection, setOpenSection] = useState(null);

  const toggleSection = (sectionTitle) => {
    setOpenSection(openSection === sectionTitle ? null : sectionTitle);
  };

  const renderExpandableSection = (title, content) => (
    <section
      className={`border-b border-gray-300 py-6 cursor-pointer transition-colors ${
        openSection === title ? "bg-gray-50" : ""
      }`}
      onClick={() => toggleSection(title)}
    >
      <h2 className="flex justify-between items-center text-lg md:text-xl font-semibold text-[#072447] mb-0 select-none">
        {title}
        <span className="arrow-icon ml-2 text-[#072447] text-base md:text-lg">
          {openSection === title ? "▲" : "▼"}
        </span>
      </h2>
      <div
        className={`mt-4 text-[#333] text-base md:text-lg leading-relaxed ${
          openSection === title ? "block" : "hidden"
        }`}
      >
        {content}
      </div>
    </section>
  );

  return (
    <>
      <div className="privacy-policy-container bg-white text-[#333] min-h-screen py-8 font-sans">
        <div className="privacy-policy-content max-w-4xl mx-auto px-6 md:px-10">
          <h1 className="font-bold text-4xl md:text-5xl text-[#072447] mb-2 text-left border-b border-gray-300 pb-4 font-primary">
            Privacy Policy
          </h1>

          <div className="last-updated text-sm text-gray-600 text-left mt-2 mb-10 pt-0 border-t-0">
            Updated on 1st October, 2025
          </div>

          <section className="mb-10">
            <p className="text-base md:text-lg text-[#333] leading-relaxed mb-5 text-left">
              FOZZIL respects your privacy and is committed to protecting your
              personal data. This Privacy Policy explains how FOZZIL collects,
              uses, and shares information when you use our services or interact
              with our website.
            </p>
          </section>

          <img
            src="https://img1.wsimg.com/isteam/ip/b0b3e65e-c760-4844-b125-253596bb7178/blob-b5316e5.png/:/cr=t:5.93%25,l:2.83%25,w:94.34%25,h:88.14%25/rs=w:365,h:365,cg:true,m"
            alt="Privacy Icon"
            className="privacy-policy-image block mx-auto my-8 max-w-md w-full h-auto"
          />
          <p className="text-base md:text-lg text-[#333] leading-relaxed mb-5 text-left">
            Before using these features, you'll be given the opportunity to
            review this product-specific information. You can also view this
            information at any time in the relevant settings or by visiting our
            website at fozzil.in/privacy.
          </p>

          <section className="privacy-policy-contents mb-10">
            <h2 className="text-xl md:text-2xl font-semibold text-[#072447] mb-6 text-left">
              Contents
            </h2>
            <ul className="list-disc ml-8 mb-5">
              <li>Data & Privacy Information</li>
              <li>Your Privacy Rights</li>
              <li>Product-Specific Privacy Information</li>
              <li>Cookies and Tracking Technologies</li>
              <li>Data Security</li>
              <li>Privacy Policy Updates</li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-xl md:text-2xl font-semibold text-[#072447] mb-4 text-left">
              Data & Privacy Information
            </h2>
            <p className="text-base md:text-lg text-[#333] leading-relaxed mb-3 text-left">
              We collect and use personal data to improve your experience with
              FOZZIL's services and products. Depending on the features you use,
              you may be asked to provide personal information, such as your
              name, email address, or IP address. FOZZIL ensures that your data
              is used only as specified in this policy and within
              product-specific guidelines.
            </p>
            <p className="text-base md:text-lg text-[#333] leading-relaxed mb-3 text-left">
              You can view our product-specific privacy practices in your
              settings or at any time by visiting fozzil.in/Privacy Policy.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-xl md:text-2xl font-semibold text-[#072447] mb-4 text-left">
              Your Privacy Rights
            </h2>
            <p className="text-base md:text-lg text-[#333] leading-relaxed mb-3 text-left">
              FOZZIL acknowledges your rights to:
            </p>
            <ul className="list-disc ml-8 mb-3">
              <li>Access, correct, or delete your personal data.</li>
              <li>Limit or object to how your personal data is processed.</li>
              <li>Withdraw consent at any time.</li>
            </ul>
            <p className="text-base md:text-lg text-[#333] leading-relaxed mb-3 text-left">
              If you have any concerns or requests about your personal data,
              please contact us at privacy@fozzil.in.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-xl md:text-2xl font-semibold text-[#072447] mb-4 text-left">
              Product-Specific Privacy Information
            </h2>
            <p className="text-base md:text-lg text-[#333] leading-relaxed mb-3 text-left">
              Certain FOZZIL products and services come with specific data and
              privacy guidelines. These guidelines are designed to give you
              transparency and control over how your data is collected and used.
            </p>
            <p className="text-base md:text-lg text-[#333] leading-relaxed mb-3 text-left">
              Examples of product-specific data usage include:
            </p>
            <ul className="list-disc ml-8 mb-3">
              <li>
                FOZZIL Intelligence: User input data and voice recordings may be
                stored to improve the accuracy of the model output.
              </li>
              <li>
                ZYPHER AI : Personal health data may be stored for tracking and
                analytics purposes but will always require your explicit
                consent.
              </li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-xl md:text-2xl font-semibold text-[#072447] mb-4 text-left">
              Data Security
            </h2>
            <p className="text-base md:text-lg text-[#333] leading-relaxed mb-3 text-left">
              FOZZIL uses state-of-the-art encryption and security practices to
              protect your personal data. However, no method of electronic
              transmission is 100% secure, and we cannot guarantee the absolute
              security of your information.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-xl md:text-2xl font-semibold text-[#072447] mb-4 text-left">
              Privacy Policy Updates
            </h2>
            <p className="text-base md:text-lg text-[#333] leading-relaxed mb-3 text-left">
              FOZZIL may occasionally update this policy to reflect changes in
              technology or legal requirements. We encourage you to review this
              page periodically for any updates. Your continued use of our
              services after changes to the Privacy Policy implies acceptance of
              those changes.
            </p>
          </section>

          {/* Accordion Sections */}
          {renderExpandableSection(
            "What Is Personal Data at FOZZIL?",
            <Fragment>
              <p>
                At FOZZIL, we firmly believe in your right to privacy, no matter
                where you live in the world. We define personal data as any data
                related to an identified or identifiable individual or any data
                that can be linked to them directly or indirectly by FOZZIL.
                This includes data such as your name or data that does not
                directly identify you but can be used to identify you, such as
                your device's serial number. Aggregated data is considered
                non-personal data for the purposes of this Privacy Policy.
              </p>
              <p>
                This Privacy Policy applies to how FOZZIL handles your personal
                data, whether you interact with us on our website, through
                FOZZIL apps, or in person. FOZZIL may also provide links to
                third-party apps or services; our Privacy Policy does not apply
                to third parties, and we encourage you to review their privacy
                practices before interacting with them.
              </p>
            </Fragment>
          )}

          {renderExpandableSection(
            "Your Privacy Rights at FOZZIL",
            <Fragment>
              <p>
                At FOZZIL, we respect your rights regarding your personal data.
                These include the rights to access, correct, restrict the
                processing of, transfer, or delete your personal data. We
                provide these rights to our global customers, and if you choose
                to exercise them, you have the right to do so without facing
                discrimination from FOZZIL.
              </p>
              <p>
                To exercise your privacy rights, visit the FOZZIL Data & Privacy
                page at fozzil.in/privacy. For the protection of your personal
                data, your identity may need to be verified before any request
                is processed. In some cases, we may not be able to fulfill your
                request — for example, if retaining certain data is necessary
                for legal obligations or fraud prevention purposes.
              </p>
              <p>
                If you live in certain regions and cannot access the FOZZIL Data
                & Privacy page, you or your authorized representative can submit
                a request through fozzil.in/contact.
              </p>
            </Fragment>
          )}

          {renderExpandableSection(
            "Personal Data FOZZIL Collects from You",
            <Fragment>
              <p>
                FOZZIL is committed to collecting only the personal data that is
                necessary for providing great products and services. The type of
                personal data FOZZIL collects depends on how you interact with
                us. When you create an account, contact customer support, or
                interact with our services, we may collect various types of
                information, including:
              </p>
              <ul>
                <li>
                  Device Information: Information such as device serial numbers
                  and browser types that can identify your device.
                </li>
                <li>
                  Contact Information: Your name, email, phone number, and
                  physical address.
                </li>
                <li>
                  Payment Information: Billing information including payment
                  methods (e.g., bank details, credit/debit card details).
                </li>
                <li>
                  Transaction Information: Data about purchases made through
                  FOZZIL platforms and services.
                </li>
                <li>
                  Usage Data: Data related to your usage of FOZZIL services such
                  as browsing history, product interactions, and diagnostics.
                </li>
                <li>
                  Location Information: Precise or coarse location data for
                  region-specific services or features.
                </li>
                <li>
                  Fraud Prevention Information: Data used to identify and
                  prevent fraud.
                </li>
                <li>
                  Health & Fitness Information: Personal health data, including
                  fitness details shared with FOZZIL services.
                </li>
                <li>
                  Government ID Data: In some regions, we may collect
                  government-issued IDs to set up accounts or as required by
                  law.
                </li>
                <li>
                  Other Information You Provide: This includes any content or
                  information you share during interactions with customer
                  support or through social media.
                </li>
              </ul>
              <p>
                If you choose not to provide certain personal data, FOZZIL may
                be unable to offer some products or services or respond to your
                requests.
              </p>
            </Fragment>
          )}

          {renderExpandableSection(
            "Personal Data FOZZIL Receives from Other Sources",
            <Fragment>
              <p>Individuals:</p>
              <p>
                FOZZIL may collect data about you from other individuals if they
                send you products, invites, or share content with you related to
                FOZZIL services.
              </p>
              <p>At Your Direction:</p>
              <p>
                You may direct third parties to share data with FOZZIL, such as
                when using third-party services integrated with FOZZIL's
                products.
              </p>
              <p>FOZZIL Partners:</p>
              <p>
                FOZZIL may validate information with partners for security and
                fraud-prevention purposes.
              </p>
            </Fragment>
          )}

          {renderExpandableSection(
            "FOZZIL's Use of Personal Data",
            <Fragment>
              <p>Power Our Services:</p>
              <p>
                FOZZIL collects necessary data to improve services, perform
                internal audits, and troubleshoot issues.
              </p>
              <p>Process Your Transactions:</p>
              <p>
                To complete transactions, FOZZIL collects details like your name
                and payment information.
              </p>
              <p>Communicate with You:</p>
              <p>
                FOZZIL may contact you regarding your account, transactions, or
                important notices.
              </p>
              <p>Security and Fraud Prevention:</p>
              <p>
                FOZZIL protects against fraud and scans uploaded content for
                illegal material.
              </p>
              <p>Personalization:</p>
              <p>
                If you opt for personalization, FOZZIL will use collected data
                to enhance your experience.
              </p>
              <p>Comply with Law:</p>
              <p>FOZZIL may process your data as required by applicable law.</p>
            </Fragment>
          )}

          {renderExpandableSection(
            "FOZZIL's Sharing of Personal Data",
            <Fragment>
              <p>Service Providers:</p>
              <p>
                FOZZIL may engage third parties to act on its behalf, such as
                processing data and delivering products. These providers must
                handle data in compliance with FOZZIL's policies.
              </p>
              <p>Partners:</p>
              <p>
                FOZZIL partners with third parties for services or financial
                offerings, requiring partners to protect your data.
              </p>
              <p>Others:</p>
              <p>
                FOZZIL may share data if legally required or necessary for
                security or operational protection.
              </p>
            </Fragment>
          )}

          {renderExpandableSection(
            "Protection of Personal Data at FOZZIL",
            <Fragment>
              <p>
                FOZZIL uses robust administrative, technical, and physical
                safeguards to secure personal data and continuously improves
                these measures.
              </p>
            </Fragment>
          )}

          {renderExpandableSection(
            "Children and Personal Data",
            <Fragment>
              <p>
                FOZZIL takes extra care to protect children's data, with special
                provisions for accounts managed by parents or educational
                institutions.
              </p>
            </Fragment>
          )}

          {renderExpandableSection(
            "Cookies and Other Technologies",
            <Fragment>
              <p>
                FOZZIL's website, online services, and interactive applications
                may use cookies and other technologies like web beacons. These
                technologies help us better understand user behavior, including
                for security and fraud prevention purposes, indicate which
                sections of our website users visit, and facilitate and measure
                the effectiveness of advertisements and web searches.
              </p>
              <p>Communications Cookies:</p>
              <p>
                These cookies allow network traffic between FOZZIL's systems and
                your device. They also help us detect any errors.
              </p>
              <p>Strictly Necessary Cookies:</p>
              <p>
                These cookies are essential to providing specific features or
                services you have requested. For instance, they enable us to
                display the website in the proper format, authenticate your
                transactions, and preserve the contents of your shopping cart.
              </p>
              <p>Other Cookies:</p>
              <p>
                These cookies help us understand how visitors interact with our
                website and online services. FOZZIL uses these cookies to assess
                the effectiveness of advertisements and web searches, as well as
                to remember your preferences, providing a customized experience.
              </p>
              <p>
                If you prefer that FOZZIL not use cookies, we provide options to
                disable them. If you are using a web browser, you can modify
                your browser settings to block all cookies. Please note, certain
                features of the FOZZIL website may not be available if you
                disable cookies.
              </p>
              <p>
                In addition to cookies, FOZZIL may use other technologies to
                achieve similar objectives, such as tracking URLs in emails to
                monitor interest in particular content. If you prefer not to be
                tracked in this way, you should avoid clicking graphic or text
                links in email messages.
              </p>
              <p>
                FOZZIL treats data collected via cookies and similar
                technologies as non-personal data. However, in jurisdictions
                where IP addresses or similar identifiers are considered
                personal data, we treat them as such. We may combine data
                collected from cookies with other personal data FOZZIL holds,
                treating the combined data as personal.
              </p>
              <p>
                FOZZIL does not track users or collect data for advertising
                purposes through third-party platforms or link personal
                information across other companies.
              </p>
              <p>
                If you do not want to receive ads targeted to your interests
                from FOZZIL's advertising platforms, you can disable
                personalized ads through your device settings. You may still see
                ads based on the context of your activity on our website.
              </p>
            </Fragment>
          )}

          {renderExpandableSection(
            "Transfer of Personal Data Between Countries",
            <Fragment>
              <p>
                FOZZIL is committed to connecting users worldwide with our
                products and services. This may involve transferring your
                personal data to or accessing it from entities around the globe,
                including FOZZIL-affiliated companies. We comply with relevant
                data protection laws to ensure that your data is protected no
                matter where it is processed.
              </p>
              <p>
                The control of your personal data may vary based on your
                location. For example, personal data related to FOZZIL's
                e-commerce services may be controlled by different entities in
                your region. Regardless of where your data is processed or
                stored, FOZZIL maintains the same high standards of protection.
              </p>
              <p>
                For personal data originating in regions like the European
                Economic Area (EEA) or the United Kingdom, FOZZIL ensures that
                cross-border transfers comply with applicable regulations and
                are governed by Standard Contractual Clauses or other legal
                mechanisms.
              </p>
              <p>
                If you have questions about how FOZZIL processes your data or
                wish to understand more about international data transfers, you
                can contact us through our support channels.
              </p>
            </Fragment>
          )}

          <section className="privacy-policy-contact">
            <h2>Contact Us</h2>
            <p>
              If you have any questions or concerns about this Privacy Policy,
              please contact us at: FOZZIL,Chennai- 603203 Tamil Nadu, India
              privacy@fozzil.in
            </p>
          </section>
        </div>
      </div>
    </>
  );
};

export default PrivacyPolicy;
